import React, { useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pencil, Trash2, Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/format";
import { ProductDialog, PackageDialog } from "@/components/catalogue/CatalogueDialogs";

export default function ManagePackagesDialog({ open, onOpenChange, products, skus, onChange, canCreate, canEdit, canDelete }) {
  const [prodDialog, setProdDialog] = useState(null);
  const [pkgDialog, setPkgDialog] = useState(null);

  const remove = async (kind, item) => {
    const label = item.code || item.name;
    if (!window.confirm(`Delete ${kind} ${label}?`)) return;
    try {
      await api.delete(`/catalogue/${kind === "product" ? "products" : "skus"}/${item.id}`);
      toast.success(`${kind} deleted`);
      onChange?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "This item is linked to existing transactions and cannot be deleted.");
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" data-testid="manage-packages-dialog">
          <DialogHeader><DialogTitle>Manage Products & Packages</DialogTitle></DialogHeader>
          <Tabs defaultValue="products">
            <TabsList className="mb-3">
              <TabsTrigger value="products" data-testid="manage-tab-products">Products</TabsTrigger>
              <TabsTrigger value="packages" data-testid="manage-tab-packages">Packages</TabsTrigger>
            </TabsList>

            <TabsContent value="products">
              {canCreate && (
                <div className="flex justify-end mb-2">
                  <Button size="sm" onClick={() => setProdDialog({})} data-testid="manage-new-product">
                    <Plus className="w-3.5 h-3.5 mr-1" /> New product
                  </Button>
                </div>
              )}
              <div className="border border-slate-200 rounded-md">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead className="w-32">Code</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-24 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-6">No products yet.</TableCell></TableRow>}
                    {products.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="font-mono text-xs">{p.code}</TableCell>
                        <TableCell>{p.name}</TableCell>
                        <TableCell className="text-xs text-slate-500 truncate max-w-md" title={p.description}>{p.description || "—"}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-0.5">
                            {canEdit && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setProdDialog({ product: p })}><Pencil className="w-3.5 h-3.5" /></Button>}
                            {canDelete && <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-rose-50" onClick={() => remove("product", p)}><Trash2 className="w-3.5 h-3.5 text-rose-600" /></Button>}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="packages">
              {canCreate && (
                <div className="flex justify-end mb-2">
                  <Button size="sm" onClick={() => setPkgDialog({})} data-testid="manage-new-package">
                    <Plus className="w-3.5 h-3.5 mr-1" /> New package
                  </Button>
                </div>
              )}
              <div className="border border-slate-200 rounded-md">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead className="w-32">Code</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead className="w-40 text-right">Price (MYR)</TableHead>
                      <TableHead className="w-24 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {skus.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-6">No packages yet.</TableCell></TableRow>}
                    {skus.map((s) => {
                      const p = products.find((x) => x.id === s.product_id);
                      const priceMinor = s.default_price_minor || 0;
                      return (
                        <TableRow key={s.id} data-testid={`manage-package-row-${s.code}`}>
                          <TableCell className="font-mono text-xs">{s.code}</TableCell>
                          <TableCell>{s.name}</TableCell>
                          <TableCell className="text-xs text-slate-500">{p ? `${p.code} · ${p.name}` : "—"}</TableCell>
                          <TableCell className="text-right">
                            {priceMinor > 0 ? (
                              <span className="font-mono text-sm tabular-nums" data-testid={`manage-package-price-${s.code}`}>{fmtMoney(priceMinor, "MYR")}</span>
                            ) : (
                              <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 text-[10px]" data-testid={`manage-package-nprice-${s.code}`}>
                                <AlertTriangle className="w-3 h-3 mr-1 inline" /> No price set
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-0.5">
                              {canEdit && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPkgDialog({ pkg: s })}><Pencil className="w-3.5 h-3.5" /></Button>}
                              {canDelete && <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-rose-50" onClick={() => remove("sku", s)}><Trash2 className="w-3.5 h-3.5 text-rose-600" /></Button>}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <ProductDialog
        open={!!prodDialog}
        onOpenChange={(v) => { if (!v) setProdDialog(null); }}
        product={prodDialog?.product}
        onDone={() => { setProdDialog(null); onChange?.(); toast.success(prodDialog?.product ? "Product updated" : "Product created"); }}
      />
      <PackageDialog
        open={!!pkgDialog}
        onOpenChange={(v) => { if (!v) setPkgDialog(null); }}
        pkg={pkgDialog?.pkg}
        products={products}
        onDone={() => { setPkgDialog(null); onChange?.(); toast.success(pkgDialog?.pkg ? "Package updated" : "Package created"); }}
      />
    </>
  );
}
